/**
 * Dex live frame adapter — Push 7 / v1.4.2 b3.
 *
 * Pure, side-effect-free conversion from a `PokedexGridFrame` (the output
 * of `extractPokedexFrame`) into the Pokédex-only `VariantCellObservation`
 * shape consumed by `dexStateStore.applyScanObservations`. This is the
 * Dex-only equivalent of the legacy `ScanPokedexModal.confirm()` mapping
 * step, but isolated so the live-scan hook can call it on every broadcast
 * frame without dragging the upload modal in.
 *
 * Design rules (mirror the Push 6 constraints):
 *   - The 4×4 visible Pokémon GO grid is the only mode we recognise here.
 *     Region-overview screens return `mode: "region_overview"` and no
 *     observations — the live scanner skips applying them, the diagnostic
 *     panel still surfaces what the user pointed the camera at.
 *   - Sparse variants never imply missing entries: we only emit cells
 *     for dex numbers actually parsed in the frame.
 *   - Cells in the bottom overlay band (yPct >= 0.86) are NOT emitted as
 *     "missing" — present cells in that band are still emitted because
 *     the sprite was clearly visible.
 *   - Variant inference: prefer the OCR-detected filter; fall back to the
 *     caller's active page variant. Confidence is borrowed from the
 *     classifier wrapper so the diagnostic panel can show a single number.
 *   - No fabrication: if no dex numbers were parsed, the adapter returns
 *     `variant: null` and `mode: "unknown"`, leaving the live scan loop
 *     to skip the frame.
 */

import type {
  PokedexGridFrame,
  PokedexFilter,
  PokedexWarning,
} from "./pokedexOcr";
import { isProgressInRange } from "./pokedexOcr";
import {
  boundaryRecoveredVariant,
  colourFamilyVetoesVariant,
  isCellInOverlayBand,
  soleVariantForColourFamily,
  COLOUR_RESOLVE_CHIP_REQUIRED,
  type CellState,
  type PokedexRegionLite,
  type PokedexVariant,
  type VariantCellObservation,
} from "./pogoPokedexVariantClassifier";
import { resolveVariant } from "./variantResolution";
import type { DexScanMode } from "./dexScanDiagnostics";
import { resolveCellNationalDex } from "./pogoSlotMapping";
import { sanitizeFrameObservations } from "./dexOcrSanity";
import { validateDexMember, curatedRosterFor } from "./pogoAvailability";
import { containsNexusOverlayMarker } from "./nexusOverlayGuard";
import {
  resolveTabPillSignal,
  voteVariant,
  type TabPillLine,
  type TabVariant,
  type TintSample,
  type TintSignal,
  type VariantSignalSource,
} from "./variantSignals";
import { DEX_SCAN_VERBOSE } from "./dexScanLog";
import { dexExtraFormIdsFor } from "./megaGmaxSprites";

/**
 * Y-axis geometry of the live Pokémon GO Pokédex grid. The live ReplayKit
 * broadcast shows 4 columns × 4 visible rows (vs the 6-row upload sweep),
 * so live cells are taller. `rowIndex` 0-3 maps to band centres roughly
 * evenly between the header (~0.17) and the bottom carousel (~0.86).
 */
const GRID_START_Y = 0.17;
/** 4 visible rows spanning ~0.17→0.81 → ~0.16 per row. */
const CELL_HEIGHT = 0.16;
/** rowIndex above this is in the bottom overlay/carousel band — fence it. */
const MAX_VISIBLE_ROW = 3;
/**
 * Default PoGo Pokédex grid width — 4 columns for every variant EXCEPT G-Max,
 * which renders 3 wider columns. The slot-index stride MUST match the column
 * count the frame's geometry was actually swept with (see
 * `slotStrideForFrame`); using a fixed 4 on a 3-col G-Max frame shifts every
 * row past the first by an accumulating off-by-(rowIndex) error.
 */
const DEFAULT_GRID_WIDTH = 4;
/** G-Max renders 3 wider columns; mirrors dexOcrRoi.GMAX_GRID_COLUMNS. */
const GMAX_GRID_WIDTH = 3;

/**
 * The grid column stride to use when converting a cell's (rowIndex, colIndex)
 * into a 1-based slot index. Prefer the column count the frame's geometry was
 * actually cropped with (`frame.gridColumns`, set by extractPokedexFrame from
 * `gridColumnsForVariant(frameVariant)`); this is the single trusted signal so
 * slot arithmetic and ROI extraction can never disagree about the grid shape.
 *
 * Fallbacks, in order, keep every existing caller / older deserialized frame
 * bit-for-bit unchanged:
 *   1. `frame.gridColumns` when present (the live path, post this PR).
 *   2. the frame's `variantChip` variant when it is the only signal that the
 *      page is G-Max (defensive: a frame produced before this field existed but
 *      still carrying a confident gmax chip).
 *   3. the widest encoded `colIndex + 1` across the cells (so a 3-col frame can
 *      never be strided as 4-col even with no metadata at all), and finally
 *   4. the 4-col default.
 */
export function slotStrideForFrame(frame: PokedexGridFrame): number {
  if (typeof frame.gridColumns === "number" && frame.gridColumns > 0) {
    return frame.gridColumns;
  }
  if (frame.variantChip?.variant === "gmax") return GMAX_GRID_WIDTH;
  let maxColExclusive = 0;
  for (const c of frame.cells) {
    if (c.dexNumber == null) continue;
    if (c.rowIndex > MAX_VISIBLE_ROW) continue;
    if (c.colIndex + 1 > maxColExclusive) maxColExclusive = c.colIndex + 1;
  }
  if (maxColExclusive > DEFAULT_GRID_WIDTH) return maxColExclusive;
  return DEFAULT_GRID_WIDTH;
}

/** Map the legacy PokedexFilter enum into the new PokedexVariant id. */
export function filterToVariant(filter: PokedexFilter): PokedexVariant {
  switch (filter) {
    case "shiny": return "shiny";
    case "lucky": return "lucky";
    case "shadow": return "shadow";
    case "purified": return "purified";
    case "three_star": return "perfect";
    case "xxl": return "xxl";
    case "xxs": return "xxs";
    case "caught":
    case "seen":
    default:
      return "pokemon";
  }
}

/**
 * Compact, side-effect-free description of how strongly a frame looks
 * like a real Pokémon GO Pokédex grid. Consumed by `dexFrameGate` to
 * reject overlay / broadcast-picker / non-grid frames before they can
 * mutate Dex state. Carries the OCR text + warnings the gate needs.
 */
export interface DexGridSignature {
  /** Raw warnings the OCR pipeline emitted for this frame. */
  warnings: PokedexWarning[];
  /** Concatenated header/tab/filter OCR text, for overlay-token matching. */
  text: string;
  /** Dex numbers parsed in this frame. */
  numbersRead: number;
  /** Distinct grid rows that yielded a dex number (overlays span 0-1 rows). */
  distinctRows: number;
  /** Region tab was readable (we're inside a Pokédex). */
  regionKnown: boolean;
  /**
   * The region came from TEXT (header label, tab text, top panel), not from
   * the tab-bar column pixel guess (2026-09-23). `detectActiveColumn` names a
   * column on any screen with a dark band at the tab-bar height, so a pixel
   * guess is not Pokédex chrome. Optional for older fixtures: absent means
   * "as regionKnown".
   */
  regionRead?: boolean;
  /** Filter chip was readable. */
  filterKnown: boolean;
  /** Header progress total was present and in range for the region. */
  progressInRange: boolean;
}

/**
 * G-Max QA gate-marker counts for a single adapted frame. Each field is the
 * number of present observations that the matching PR #461 gate vetoed on this
 * frame. `twoFramePending` / `twoFrameConfirmed` are NOT counted here — those
 * decisions are made later in `dexLiveScanSession` (the adapter has no cross-
 * frame memory), so the session adds them to its own aggregate.
 */
export interface DexFrameGateMarkers {
  /** present-write blocked (invalid dex member) — roster membership veto. */
  invalidDexMember: number;
  /** present-write blocked (gmax weak sprite evidence) — G-Max silhouette veto. */
  gmaxWeakSprite: number;
  /**
   * gmax weak evidence deferred — weak-but-plausible G-Max present sightings
   * emitted as "present" + `weakGmaxEvidence` and routed to the session's
   * stricter 3-frame staged confirmation. NOT a veto: counts how many plausible
   * catches were held for corroboration instead of dropped.
   */
  gmaxWeakDeferred: number;
  /** frame present-writes blocked (overlay text) — Nexus HUD text veto. */
  overlayTextBlock: number;
  /**
   * Blocked Shadow/Purified reads for this frame — a Shadow/Purified sighting
   * whose dex id is NOT in the curated roster (so the present-write was vetoed).
   * Fed to the session's reader self-heal, which reports high-confidence,
   * multi-frame-confirmed blocks so the roster can catch up to new releases.
   */
  blockedShadowReads: BlockedShadowRead[];
}

/** A single blocked Shadow/Purified read, surfaced for reader self-heal. */
export interface BlockedShadowRead {
  /** National dex number of the blocked sighting. */
  dexId: number;
  /** Which variant the reader saw. */
  variant: "shadow" | "purified";
  /** Frame OCR confidence (0-100). */
  confidence: number;
}

export interface DexLiveFrameResult {
  /** Detected Dex variant, or null when nothing was readable. */
  variant: PokedexVariant | null;
  /** True when we fell back to the caller's `initialVariant`. */
  variantInferred: boolean;
  /** Region picked up by the legacy OCR header crop, or "unknown". */
  region: PokedexRegionLite;
  /** Screen shape the frame represented. */
  mode: DexScanMode;
  /** OCR confidence (0-100), straight from the frame. */
  confidence: number;
  /** Dex-only observations to feed into applyScanObservations(). */
  observations: VariantCellObservation[];
  /** Cells the OCR reported in total. */
  cellsObserved: number;
  /** Cells with a parsed dex number (subset of cellsObserved). */
  numbersRead: number;
  /** Cells the OCR returned but no dex number was parsed. */
  unknownCells: number;
  /**
   * Cells this frame that the OVERLAY GUARD skipped — number ROI or sprite body
   * under our own HUD card / PiP — so they emitted no observation at all.
   *
   * Surfaced because the PiP is USER-RESIZABLE: at its largest it covers most of
   * the grid, and every covered cell is silently skipped while the HUD still says
   * "Scanning…". The guard makes that SAFE (a skipped cell cannot become a write)
   * but not VISIBLE, which is the failure a big HUD actually produces. This is
   * what lets the session coach "shrink the window" instead of leaving the
   * trainer scrolling past a dex that nothing is reading.
   *
   * Optional so the many hand-built frame fixtures stay valid; consumers read it
   * as `?? 0`, which is also the honest default for a frame that never ran the
   * guard (upload path, older captures).
   */
  overlayObscuredCells?: number;
  /**
   * PR-E6-OCR3 — counters for the sprite-confidence apply gate. Telemetry
   * only: present-state cells whose sprite-matcher confidence fell into the
   * review band (>= SPRITE_REVIEW_FLOOR && < SPRITE_APPLY_THRESHOLD) are
   * surfaced for a future review queue, while cells below the floor are
   * silently dropped. Cells without a sprite verdict (upload path / older
   * frames without per-cell cellCrop) pass through unchanged for back-compat.
   */
  spriteGate: {
    /** Caught cells the gate auto-applied (>= SPRITE_APPLY_THRESHOLD). */
    autoApplied: number;
    /** Caught cells held for review (>= SPRITE_REVIEW_FLOOR but < threshold). */
    queuedForReview: number;
    /** Caught cells dropped (< SPRITE_REVIEW_FLOOR or matcher "uncertain" low-conf). */
    dropped: number;
    /** Caught cells with no sprite verdict — let through under back-compat. */
    legacyPassthrough: number;
  };
  /**
   * G-Max QA telemetry — structured count of the PR #461 present-write gates
   * that fired on THIS frame. These mirror the existing `console.log` markers
   * (which remain, for on-device Xcode debugging) but expose them as machine-
   * readable counters so the live-scan session can aggregate them and the
   * G-Max QA monitor can decide, per scan, whether a watched gate co-occurred
   * with a successful apply. Populating these does NOT change any gate
   * decision — every veto still downgrades the cell to "missing" exactly as
   * before; we only count what already happened.
   */
  gateMarkers: DexFrameGateMarkers;
  /** Optional human-readable warning (e.g. "low confidence"). */
  note?: string;
  /** Grid-signature summary for the pre-apply gate (Push 1 / v1.4.3). */
  gridSignature: DexGridSignature;
  /**
   * PR-C Problem 1: confidence (0..1) and provenance of the variant decision
   * from the two-signal vote (tab-pill OCR + screen tint). The diagnostic panel
   * surfaces these so the user can see WHY a variant was chosen before saving.
   */
  variantConfidence: number;
  variantSource: VariantSignalSource;
  /** Human-readable provenance, e.g. "Lucky (tab-OCR ✓ tint ✓)". */
  variantDetail: string;
  /** True when the two signals disagreed (logged for telemetry). */
  variantDisagreement: boolean;
  /**
   * True when the chip-text variant read was independently corroborated by the
   * hue check. Threaded through so resolveTrustedVariant can clear a corroborated
   * chip-text read at the lower CHIP_TEXT_CORROBORATED_THRESHOLD.
   */
  variantHueAgrees?: boolean;
}

export interface AdaptDexLiveFrameOptions {
  /** Caller's active Dex page variant. Used only when OCR could not pin one down. */
  initialVariant?: PokedexVariant;
  /**
   * Minimum confidence (0-100) for the frame to be treated as a valid
   * Dex frame. Below this threshold we still return cells but mark the
   * mode "unknown" so the caller skips applying observations.
   */
  minConfidence?: number;
  /**
   * PR-C Problem 1 — Signal A. OCR text lines cropped from the bottom variant-
   * tab-pill ROI (native layer). Each carries its own OCR confidence so the
   * highlighted (active) pill, which renders sharper, wins the vote.
   */
  tabPillLines?: TabPillLine[];
  /**
   * PR-C Problem 1 — Signal B. Dominant tint sampled from the upper ~60% dex
   * grid area (native layer).
   */
  tintSample?: TintSample;
  /**
   * Last confidently-resolved variant for this session. The vote falls back to
   * this (sticky) when both signals are inconclusive — we NEVER silently
   * default to "pokemon".
   */
  lastKnownVariant?: TabVariant | null;
  /**
   * colour-narrowed pill re-read (2026-06-19) — the page COLOUR family for THIS
   * frame (e.g. "dex_cyan"), from the hook's classifyPaletteRobust tracker. Used
   * ONLY to recover the variant when the active-pill read failed
   * (`variantChip.source === "none"`) yet the frame read real dex numbers. The
   * variant is derived from the PoGo PAGE COLOUR — never from app_state. A
   * single-variant family resolves directly (soleVariantForColourFamily); a
   * multi-variant family uses `colourNarrowedVariant` below. Undefined / non-dex
   * families leave the existing conservative drop unchanged.
   */
  pageColourFamily?: string | null;
  /**
   * colour-narrowed pill re-read result for THIS frame: the variant a WIDE,
   * colour-narrowed pill OCR re-read matched against ONLY the page family's
   * allowed tokens, or null when nothing matched. Computed by the caller
   * (awaiting `readColourNarrowedPill` in pokedexOcr) and injected here so the
   * adapter stays pure/sync. Consulted ONLY for a multi-variant family when the
   * active-pill read failed. Re-validated against the colour veto here so an
   * impossible token can never route.
   */
  colourNarrowedVariant?: PokedexVariant | null;
  /**
   * perf/scan-efficiency (2026-07-06): DUMP-ON-APPLY. Session-supplied predicate
   * answering "has this dex cell already committed to the store this session,
   * under the current scan context?". When it returns true the adapter SKIPS the
   * per-cell sprite classification / veto / observation-building pipeline for
   * that cell (the number + slot geometry are still read, cheaply, upstream) —
   * re-classifying an already-committed cell yields no new signal. The session
   * clears its applied set on context change and scroll reverse, so a skip here
   * never suppresses a cell that still needs classifying. Absent (tests / non-
   * live callers) means never skip — identical behaviour to before this change.
   */
  isCellApplied?: (dexNumber: number) => boolean;
  /**
   * Told about every cell the dump-on-apply skip elided, with its 1-based
   * lattice slot (2026-09-24). DISPLAY ONLY: the HUD mirror map needs those
   * numbers in their columns, or a row whose other cells were already applied
   * reads as a separate row. Never feeds an observation or a write.
   */
  onCellSkippedApplied?: (dexNumber: number, slotIndex: number) => void;
}

const DEFAULT_MIN_CONFIDENCE = 35;

/**
 * PR-E6-OCR3 — sprite-confidence apply gate.
 *
 * The per-cell sprite chroma matcher (cellSpriteMatcher.matchCellToSprite,
 * invoked from `pokedexOcr.ts` for cells with a parsed dex number + cellCrop)
 * returns a verdict + confidence in [0, 1]. PR-E6-OCR3 turns that confidence
 * into the WRITE gate so noisy frames cannot mutate the dex.
 *
 * Bands (applied ONLY to cells the matcher rated as a CAUGHT/"present"
 * candidate — missing/unknown cells never write anyway and are unaffected):
 *   - confidence >= SPRITE_APPLY_THRESHOLD (0.85) -> auto-apply (write).
 *   - confidence >= SPRITE_REVIEW_FLOOR (0.5) but < threshold -> drop from
 *     the write path AND increment spriteGate.queuedForReview so the live
 *     scan session and diagnostic panel can later surface them in a review
 *     UI (UI wiring is a follow-up; the gate already enforces "no write").
 *   - confidence < SPRITE_REVIEW_FLOOR (0.5) -> drop silently (gate.dropped).
 *   - spriteConfidence undefined (upload path / older frames without per-cell
 *     cellCrop) -> let through unchanged (legacyPassthrough). This preserves
 *     existing behaviour for the upload modal and back-compat tests.
 *
 * The gate runs AFTER the bottom-row fence + slot mapping so we don't waste
 * cycles on cells that wouldn't be emitted regardless. It runs BEFORE the
 * sanity pass so a dropped-trailing-digit repair cannot accidentally launder
 * a low-confidence sprite into a write.
 */
export const SPRITE_APPLY_THRESHOLD = 0.85;
export const SPRITE_REVIEW_FLOOR = 0.5;



/**
 * True when two or more PRESENT observations in a frame share a dex id. The PoGo
 * grid is strictly one species per slot, so a duplicate present id is an
 * impossible OCR anomaly (run-2: slots 10/11/12/14 all read "1" caught). Only
 * present writes are checked — repeated missing/unknown reads are harmless. Pure
 * + exported so the frame-level veto is unit-tested directly.
 */
export function hasDuplicatePresentId(
  observations: ReadonlyArray<VariantCellObservation>,
): boolean {
  const seen = new Set<number>();
  for (const obs of observations) {
    if (obs.state !== "present") continue;
    if (seen.has(obs.dexNumber)) return true;
    seen.add(obs.dexNumber);
  }
  return false;
}

/**
 * Build 6 narrowed frame-veto — the set of dex ids that appear MORE THAN ONCE
 * among the present observations. The whole-frame veto used to demote every
 * present cell when ANY duplicate existed; this lets us demote only the cells
 * whose id is actually duplicated, so the clean present cells in the same frame
 * still flow into the confirmation path.
 */
export function duplicatePresentIds(
  observations: ReadonlyArray<VariantCellObservation>,
  variant?: PokedexVariant | null,
): Set<number> {
  const seen = new Set<number>();
  const dups = new Set<number>();
  for (const obs of observations) {
    if (obs.state !== "present") continue;
    // A two-form species (Mega Charizard/Mewtwo/Raichu, G-Max Urshifu) LEGITIMATELY
    // renders two cells with the same dex number, so its repeat is not a misread —
    // exempt it, or the veto would blank both cells and neither X nor Y could ever
    // be caught. Every other id keeps the duplicate = misread rule.
    if (variant && dexExtraFormIdsFor(variant, obs.dexNumber).length > 0) continue;
    if (seen.has(obs.dexNumber)) dups.add(obs.dexNumber);
    seen.add(obs.dexNumber);
  }
  return dups;
}

/**
 * Build 2 / v1.4.6 — minimum confidence for the PoGo chip-row variant signal
 * to ROUTE a write. Below this we log `variant:"unknown"` and SKIP the frame
 * rather than defaulting to "perfect" (or any sticky Nexus-side filter) —
 * dropping a frame is always preferable to corrupting the wrong dex.
 */
const VARIANT_CHIP_MIN_CONFIDENCE = 0.6;

/**
 * colour-narrowed pill re-read (2026-06-19) — confidences for a variant
 * recovered from the page COLOUR family when the active-pill read failed.
 * A single-variant family (colour-sole) is high confidence: the page tint alone
 * fixes the variant unambiguously. The wide colour-narrowed pill re-read
 * (colour-narrowed-pill) additionally read the keyword, so it is highest.
 */
const COLOUR_SOLE_CONFIDENCE = 0.85;
const COLOUR_NARROWED_PILL_CONFIDENCE = 0.9;

/**
 * Recover a variant from the page COLOUR family when the active-pill read failed
 * (`variantChip.source === "none"`). Returns null to keep the conservative drop.
 *
 * Authority chain (page colour ONLY — never app_state):
 *   1. single-variant family (peach→perfect, mint→purified, shadow_purple→shadow)
 *      → resolve directly via soleVariantForColourFamily. `colour-sole`.
 *   2. multi-variant family (cyan/golden/pink) → use the WIDE colour-narrowed
 *      pill re-read result the caller injected (`colourNarrowedVariant`), but
 *      ONLY if it is allowed for the family (re-validate the veto here so an
 *      impossible token can never route). `colour-narrowed-pill`.
 *   3. otherwise (no dex family, or multi-variant with no token match) → null.
 *
 * No hardcoded variant/colour literals: every branch derives from the veto map
 * via soleVariantForColourFamily / colourFamilyVetoesVariant.
 */
function routeFromPageColour(opts: AdaptDexLiveFrameOptions): {
  variant: PokedexVariant;
  confidence: number;
  source: VariantSignalSource;
  detail: string;
} | null {
  const family = opts.pageColourFamily;
  const sole = soleVariantForColourFamily(family);
  // 2026-08-30 audit decision: variants in COLOUR_RESOLVE_CHIP_REQUIRED
  // (perfect) may never be routed from page colour ALONE - this branch runs
  // exactly when the pill read failed, so there is no chip to agree, and an
  // unreadable pill on a warm wash must HOLD rather than fill the Perfect
  // dex. Mirrors the same gate in variantResolution.resolveVariant.
  if (sole != null && !COLOUR_RESOLVE_CHIP_REQUIRED.has(sole)) {
    return {
      variant: sole,
      confidence: COLOUR_SOLE_CONFIDENCE,
      source: "colour-sole",
      detail: `${sole} (colour-sole ✓)`,
    };
  }
  // Multi-variant family: trust the wide colour-narrowed pill re-read, but only
  // when its token is actually allowed for the family (veto invariant).
  const narrowed = opts.colourNarrowedVariant ?? null;
  if (
    narrowed != null &&
    !colourFamilyVetoesVariant(family, narrowed)
  ) {
    return {
      variant: narrowed,
      confidence: COLOUR_NARROWED_PILL_CONFIDENCE,
      source: "colour-narrowed-pill",
      detail: `${narrowed} (colour-narrowed-pill ✓)`,
    };
  }
  return null;
}

/**
 * Convert a legacy PokedexGridFrame into the Dex-only live result.
 *
 * The contract: callers MUST treat `mode: "unknown"` as "do not apply",
 * and `variant: null` as "do not apply". A frame that read 0 cells with
 * dex numbers always falls into that bucket.
 */
export function adaptDexLiveFrame(
  frame: PokedexGridFrame,
  opts: AdaptDexLiveFrameOptions = {},
): DexLiveFrameResult {
  const minConfidence = opts.minConfidence ?? DEFAULT_MIN_CONFIDENCE;
  const lowConfidence = frame.confidence < minConfidence;

  // Per-cell mapping: dex number is the durable key. Pokémon GO's live
  // grid is 4 wide × 4 high visible; we trust each cell that came back
  // with a parsed dex number. We do NOT emit "missing" here — the
  // legacy OCR cannot distinguish empty silhouettes from caught entries
  // reliably, so we err on the conservative side just like the upload
  // path does.
  const observations: VariantCellObservation[] = [];
  let numbersRead = 0;
  // 2026-08-30: bottom-band "missing" assertions dropped by the overlay fence.
  let overlayFencedMissing = 0;
  const rowsWithNumber = new Set<number>();
  // GRID SIGNATURE evidence — how much grid this frame SHOWED, counted before
  // the dump-on-apply skip below removes cells we have already committed.
  //
  // These must not be conflated with `numbersRead` / `rowsWithNumber`, which
  // describe the observations we still intend to act on. `dexFrameGate` asks a
  // different question — "is this a real Pokédex grid?" — and the answer cannot
  // depend on how much of the page we happen to have written already.
  //
  // Device log 2026-07-20: on a page whose other eleven cells were applied, the
  // signature collapsed to numbersRead 1 / distinctRows 1, scoring 0.30 against
  // the gate's 0.35 floor, so the frame was rejected as `not_pokedex_grid` and
  // the one remaining cell could never commit. #3 was read caught at sprite
  // confidence 0.964 on 29 consecutive frames and never reached the apply loop.
  // Once EVERY cell was applied the frame fell to `mode: "unknown"`, which is
  // also what drove the sticky-variant hold expiries in that trace.
  let signatureNumbersRead = 0;
  const signatureRowsWithNumber = new Set<number>();
  // G-Max hardening: the slot-index stride MUST equal the column count the
  // frame's geometry was swept with. extractPokedexFrame now exposes that as
  // `gridColumns` (3 for G-Max, 4 otherwise); using it here keeps slot
  // arithmetic and ROI extraction on the SAME trusted grid shape.
  const gridStride = slotStrideForFrame(frame);
  // Diagnostic: a 3-column geometry paired with a non-G-Max variant signal (or
  // vice-versa) means the variant resolver and the geometry disagree about the
  // page — exactly the condition that would route a real G-Max page through the
  // 4-col sweep (or a 4-col page through a 3-col stride). Surface it so a device
  // log shows the conflict instead of silently mis-indexing.
  const chipVariantForStride = frame.variantChip?.variant ?? null;
  const strideLooksGmax = gridStride === GMAX_GRID_WIDTH;
  const variantLooksGmax = chipVariantForStride === "gmax";
  if (strideLooksGmax !== variantLooksGmax && chipVariantForStride != null) {
    // eslint-disable-next-line no-console
    console.warn("[dex-scan] gmax geometry/variant conflict", {
      gridStride,
      frameGridColumns: frame.gridColumns ?? null,
      chipVariant: chipVariantForStride,
      region: frame.activeRegion,
    });
  }
  const spriteGate = {
    autoApplied: 0,
    queuedForReview: 0,
    dropped: 0,
    legacyPassthrough: 0,
  };
  // G-Max QA — structured mirror of the present-write gate console markers.
  const gateMarkers: DexFrameGateMarkers = {
    invalidDexMember: 0,
    gmaxWeakSprite: 0,
    gmaxWeakDeferred: 0,
    overlayTextBlock: 0,
    blockedShadowReads: [],
  };
  // Scan overhaul v2 (Phase 3): the per-variant frame signals that fed the
  // deleted XXS / shadow / purified / silhouette recovery branches
  // (frameIsXxs, frameIsShadowPurified, frameIsPurified, frameIsSilhouetteBearing
  // and their shared shadowPurifiedVariantSignal) are gone with those branches.
  // The presence classifier decides caught/silhouette/empty directly, so the
  // apply chain no longer needs a frame-variant recovery signal.

  // Phase 1 (chronological cell processing): iterate this frame's cells in a
  // deterministic top-left -> top-right -> next-row order so the user sees dex
  // entries added in reading order rather than in whatever scatter-gun order the
  // native OCR emitted them. Sort by (rowIndex ASC, colIndex ASC); when those
  // fields are unpopulated (older/upload frames), derive an ordering key from the
  // grid geometry (rowIndex * stride + colIndex) so the sort is still stable.
  // This is a WITHIN-FRAME ordering only — frames still process as they arrive.
  const orderedCells = [...frame.cells].sort((a, b) => {
    const ka =
      a.rowIndex != null && a.colIndex != null
        ? a.rowIndex * gridStride + a.colIndex
        : Number.MAX_SAFE_INTEGER;
    const kb =
      b.rowIndex != null && b.colIndex != null
        ? b.rowIndex * gridStride + b.colIndex
        : Number.MAX_SAFE_INTEGER;
    return ka - kb;
  });
  let sequenceInFrame = 0;
  for (const c of orderedCells) {
    if (c.dexNumber == null) continue;
    // Phase 1 telemetry: emit one line per cell in chronological order so a UI /
    // device trace can animate additions top-left -> bottom-right. Verbose-gated;
    // the sequence index is monotonic within the frame (post-sort order).
    // eslint-disable-next-line no-console
    if (DEX_SCAN_VERBOSE) console.log("[dex-scan] cell processed", {
      dexId: c.dexNumber,
      rowIndex: c.rowIndex,
      colIndex: c.colIndex,
      sequenceInFrame: sequenceInFrame++,
    });
    // Live grid shows only 4 rows. A cell reported below the visible grid
    // is bottom-carousel / control-overlay text, not a Pokédex entry —
    // skip it rather than apply a spurious number.
    if (c.rowIndex > MAX_VISIBLE_ROW) continue;
    // Build 2 / v1.4.6 — PoGo grid slot → National Dex id.
    //
    // PoGo restarts the *visual* slot numbering at 1 inside each region tab,
    // so the cell's grid position (1-based within the visible page) must be
    // translated through the per-region mapping. When OCR read a National Dex
    // number that is already valid for the region we trust it; otherwise we
    // recover the id from the slot position (the "slot index used as id" bug,
    // e.g. region=johto but cell number 1 → #152 Chikorita).
    const slotIndex = c.rowIndex * gridStride + c.colIndex + 1;
    const resolved = resolveCellNationalDex({
      region: frame.activeRegion,
      slotIndex,
      ocrNumber: c.dexNumber,
    });
    if (!resolved) continue;
    const dexNumber = resolved.dexId;
    // eslint-disable-next-line no-console
    if (DEX_SCAN_VERBOSE) console.log("[dex-scan] slot map", {
      region: frame.activeRegion,
      slotIndex,
      dexId: dexNumber,
      source: resolved.source,
      // b13i Part B: log the per-cell caught read so a device log proves WHICH
      // cells were judged caught vs silhouette-only (the Ekans/Arbok evidence).
      caught: c.caught,
    });
    // perf/scan-efficiency (2026-07-06): DUMP-ON-APPLY skip. Once this dex cell
    // has committed to the store this session (under the current context) the
    // session's isCellApplied returns true; re-running the sprite classification
    // / veto / observation pipeline below would produce no new signal, so skip
    // it. The number + slot geometry above have already produced the fingerprint
    // key, and the session clears its applied set on context change / scroll
    // reverse, so this never suppresses a cell that still needs work. No
    // observation is pushed for a skipped cell (its store entry already exists),
    // preserving the invariant: same cells applied, same cells vetoed, same
    // final state — only the classification work is elided.
    // This cell carried a readable dex number in a real grid slot, which is all
    // the frame gate needs to know — record it BEFORE the dump-on-apply skip.
    signatureNumbersRead += 1;
    signatureRowsWithNumber.add(c.rowIndex);
    if (opts.isCellApplied?.(dexNumber)) {
      opts.onCellSkippedApplied?.(dexNumber, slotIndex);
      // eslint-disable-next-line no-console
      if (DEX_SCAN_VERBOSE) console.log("[dex-scan] cell skipped (applied)", {
        dexId: dexNumber,
      });
      continue;
    }
    const yPct = GRID_START_Y + (c.rowIndex + 0.5) * CELL_HEIGHT;
    rowsWithNumber.add(c.rowIndex);
    // b18b: map the OCR engine's per-cell 3-state read (PR #194's
    // {caught, seen}) onto a Dex observation state. Pokémon GO encodes three
    // visual cell states and Nexus must mirror them exactly:
    //   - CAUGHT (full-colour sprite)        → caught:true            → "present"
    //   - AVAILABLE-UNCAUGHT (flame silhouette, number pinned bottom)
    //                                         → caught:false, seen:true  → "missing"
    //   - NEVER-SEEN (blank cell, number only, vertically centred)
    //                                         → caught:false, seen:false → "unknown"
    //
    // The NEVER-SEEN case is the launch-critical b18b fix. Build 202 (and the
    // pre-b18b adapter) stamped EVERY parsed cell `present`, so empty shadow
    // cells whose dex number was still printed and OCR'd — e.g. 023 Ekans and
    // 024 Arbok, which are never-seen in the real PoGo Shadow Dex — were
    // recorded as CAUGHT. The empty cell's number is read, but its sprite read
    // is `caught:false, seen:false`; emitting "unknown" makes the store's
    // additive apply path (`applyScanObservations` drops "unknown") record
    // nothing for it, so a never-seen cell stays never-seen — matching PoGo.
    //
    // "missing" (available-but-uncaught silhouette) still never mutates Dex
    // state on its own: the apply path only writes "present", so "missing"
    // feeds the not-caught reconciliation/conflict stream only. The
    // one-directional cascade (variant-caught ⇒ main-caught) is preserved
    // because only "present" ever reaches `ensureMainCaughtFromCascade`.
    //
    // Back-compat: frames/tests predating the per-cell field carry
    // `caught === undefined` (and no `seen`). We keep treating those as
    // `present` so the main-dex capture rate is unchanged — an undefined read
    // is "no per-cell signal", distinct from an explicit never-seen read.
    //
    // SLOT-MAP PROVENANCE GATE (2026-06-20, FP/FN architectural fix):
    // `resolveCellNationalDex` returns `source: "slot-map"` when the OCR'd
    // dex digit was missing or region-inconsistent and we recovered the id
    // from the cell's grid POSITION alone. In that case the dex id is derived
    // purely from arithmetic on the slot index — there is NO OCR evidence the
    // user actually has THIS species. The per-cell `c.caught` flag still rides
    // along (sprite matcher ran on the same cell crop), but pairing a
    // position-derived id with a sprite-derived caught flag creates an
    // off-by-one/off-by-two false positive whenever the slot index is wrong
    // by even 1 cell (scroll drift, page boundary, mis-aligned ROI). The
    // device-confirmed FP pattern (real catches at #211/#212/#215 stamped as
    // #213/#214/#216 on Johto) matches this defect exactly: same sprite
    // verdict, dex id shifted by the slot-arithmetic error.
    //
    // Rule: only OCR-sourced ids may write `state = "present"`. Slot-map ids
    // can only ever emit "missing" (never-seen / available-uncaught), so they
    // feed the not-caught reconciliation stream but cannot mutate the dex.
    // This is consistent with the OCR-anchored design of the rest of the
    // pipeline (pokedexOcr.ts trusts the printed national-dex digits as the
    // durable key; slot-map exists to RECOVER an id, not to ASSERT a catch).
    // G-Max 3-col hardening: a slot index past the page's own cell count
    // (gridStride × visible rows) can only come from a stride/geometry mismatch
    // (the run-2 gmax frames logged slotIndex 13/15 on a 12-cell 3-col page).
    // A present write from an out-of-range slot is never trustworthy — the cell
    // is mis-indexed — so it may not write present. (OCR-sourced ids still carry
    // their real dex digit, so this only fences the *write*, not the id.)
    const maxSlotIndex = gridStride * (MAX_VISIBLE_ROW + 1);
    const slotOutOfRange = slotIndex > maxSlotIndex;

    // ── Presence-classifier state decision (scan overhaul v2, Phase 3) ──────
    //
    // The Tesseract-era recovery/veto stack (gmax eligibility, XXS/shadow/
    // purified silhouette + aura recoveries, per-variant hueVariance override,
    // 3-frame weakGmaxEvidence staging) has been DELETED. Those functions only
    // existed to compensate for the hue-histogram fingerprint matcher's
    // fragility. The new presence-density classifier (cellPresence.ts) emits a
    // clean spriteState (caught/silhouette/empty) with no chroma-match /
    // fingerprint reasons, so the decision collapses to:
    //
    //   caught:false            -> "unknown" (never-seen) or "missing" (seen)
    //   slot out of range       -> "missing" (geometry sanity, KEPT)
    //   slot-map provenance     -> "missing" (no OCR evidence for this id, KEPT)
    //   otherwise               -> "present"
    //
    // Scan overhaul v2 (2026-07-08): the sprite-state write-veto
    // (spriteStateVetoesPresentWrite) and the Fix C hueVariance-floor override
    // (silhouetteWriteVetoOverridden) were DELETED. Both fired 0× and rested on
    // the legacy hue-classifier signals; leaving the veto would re-drop the
    // catches C1 newly promotes via the authoritative chroma verdict. The
    // cellPresence chroma verdict is authoritative for caught; the sole
    // false-write guard is the session's two-frame same-slot/same-dexId
    // confirmation (Fix B). KEPT gates below catch genuinely different bugs (real
    // geometry/provenance, not classifier fragility): slotOutOfRange, slot-map
    // provenance. The frame-level duplicate-id veto + OCR sanity pass run
    // downstream unchanged.
    let state: CellState;
    if (c.caught === false) {
      state = c.seen === false ? "unknown" : "missing";
    } else if (slotOutOfRange) {
      state = "missing";
      // eslint-disable-next-line no-console
      console.log("[dex-scan] present-write blocked (slot out of range)", {
        dexId: dexNumber,
        slotIndex,
        maxSlotIndex,
        gridStride,
        region: frame.activeRegion,
      });
    } else if (resolved.source === "slot-map") {
      // No OCR evidence for THIS dex id — downgrade to "missing". The cell may
      // still be a real catch, but we will not write it without OCR
      // corroboration. The next frame that reads the digits cleanly applies it.
      state = "missing";
      // eslint-disable-next-line no-console
      console.log("[dex-scan] slot-map present-write blocked", {
        dexId: dexNumber,
        slotIndex,
        region: frame.activeRegion,
        spriteCaught: c.caught,
      });
    } else {
      state = "present";
    }

    // Sprite-confidence apply gate. Only "present" cells write to the dex store
    // (the additive applyScanObservations path drops everything else), so the
    // gate only filters the present band. With the presence classifier there is
    // no weakGmaxEvidence staging: a present cell either clears the review floor
    // (auto-apply / promote to the session's normal 2-frame streak) or is
    // dropped. The session's PiP-overlap guard + 2-frame corroboration are the
    // false-write defence now, not per-frame staging.
    if (state === "present") {
      if (c.spriteConfidence == null) {
        // No sprite verdict (upload path / older frames). Back-compat: pass.
        spriteGate.legacyPassthrough += 1;
      } else if (c.spriteConfidence >= SPRITE_APPLY_THRESHOLD) {
        spriteGate.autoApplied += 1;
      } else if (c.spriteConfidence >= SPRITE_REVIEW_FLOOR) {
        // Review band (>= SPRITE_REVIEW_FLOOR, < SPRITE_APPLY_THRESHOLD). Vision
        // reads the dex number at ~100% confidence, so a review-band presence
        // verdict is authoritative: promote the cell to a full present write and
        // let the session's normal 2-frame corroboration confirm it.
        spriteGate.queuedForReview += 1;
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] sprite gate review promoted", {
          dexId: dexNumber,
          spriteConfidence: Number(c.spriteConfidence.toFixed(3)),
          spriteVerdict: c.spriteVerdict ?? null,
        });
      } else {
        spriteGate.dropped += 1;
        // eslint-disable-next-line no-console
        console.log("[dex-scan] sprite gate drop", {
          dexId: dexNumber,
          spriteConfidence: Number(c.spriteConfidence.toFixed(3)),
          spriteVerdict: c.spriteVerdict ?? null,
          band: "drop",
        });
        continue;
      }
    }

    const obs: VariantCellObservation = {
      dexNumber,
      state,
      yPct,
      slotIndex,
      ...(typeof c.spriteConfidence === "number"
        ? { spriteConfidence: c.spriteConfidence }
        : {}),
      ...(c.spriteVerdict != null ? { spriteVerdict: c.spriteVerdict } : {}),
      ...(c.spriteState != null ? { spriteState: c.spriteState } : {}),
      ...(c.numberPosition != null ? { numberPosition: c.numberPosition } : {}),
      ...(c.numberAnchored ? { numberAnchored: true } : {}),
      ...(c.spriteReason != null ? { spriteReason: c.spriteReason } : {}),
      ...(typeof c.pillOverlapFraction === "number"
        ? { pillOverlapFraction: c.pillOverlapFraction }
        : {}),
      ...(typeof c.classificationCached === "boolean"
        ? { classificationCached: c.classificationCached }
        : {}),
      // Chroma + foreground mass ride along so the evidence commit can tell a
      // small COLOUR catch the pixel classifier under-read as "empty" (real
      // chroma + body mass) from a genuinely empty grey slot (chroma ~0, aura-
      // only mass). See the evidence loop in the session.
      ...(typeof c.spriteChromaFraction === "number"
        ? { spriteChromaFraction: c.spriteChromaFraction }
        : {}),
      ...(typeof c.spriteForegroundPixels === "number"
        ? { spriteForegroundPixels: c.spriteForegroundPixels }
        : {}),
      ...(typeof c.spriteForegroundFraction === "number"
        ? { spriteForegroundFraction: c.spriteForegroundFraction }
        : {}),
      ...(typeof c.spriteBodyFrac === "number"
        ? { spriteBodyFrac: c.spriteBodyFrac }
        : {}),
      ...(typeof c.spriteLumStd === "number"
        ? { spriteLumStd: c.spriteLumStd }
        : {}),
    };
    // Overlay fencing, ACTUALLY ENFORCED (2026-08-30 audit): this used to be a
    // discarded `void isCellInOverlayBand(yPct)` while the header stated the
    // contract as fact. Same rule as cellsSafeForMissingAssertion: a bottom-
    // band cell may assert PRESENT (a visible sprite under a translucent
    // overlay is real evidence) but never MISSING (absence there is
    // indistinguishable from occlusion); the cell asserts missing once it
    // scrolls above the band. NOTE: with today's derived geometry the visible
    // rows top out at yPct 0.73, so this fence currently guards the CONTRACT
    // (against a geometry retune, an expanded-header shift, or a path that
    // supplies real screen yPcts) rather than an input reachable right now -
    // which is exactly why the discarded void call went unnoticed.
    if (obs.state === "missing" && isCellInOverlayBand(yPct)) {
      overlayFencedMissing += 1;
      numbersRead += 1; // the number WAS read; only the missing assertion is fenced
      continue;
    }
    observations.push(obs);
    numbersRead += 1;
  }

  // Build 6 / v1.4.6 — OCR misread sanity pass.
  //
  // Observations are collected in grid/slot order (ascending National Dex id).
  // A single-digit id wedged below larger in-frame neighbours is a dropped-
  // trailing-digit misread (the device log's `[17,1,19,...]` where "1" is 18,
  // and `[72,74,75,7,77,78]` where "7" is 76). Repair when the run implies a
  // unique id sharing the misread leading digit; reject otherwise. Real low-id
  // catches at the START of a run pass through untouched (capture rate kept).
  //
  // G-Max low-id exception: the official G-Max roster legitimately opens with
  // single-digit ids (#003 Venusaur / #006 Charizard / #009 Blastoise). On a
  // G-Max page those are real catches, not dropped-trailing-digit misreads, so
  // we hand the sanity pass the curated G-Max roster's low (1-9) members as the
  // `validLowIds` allow-set. A single digit IN that set is kept untouched; a
  // single digit NOT in it (e.g. a stray #31 mis-read on a G-Max page) still
  // goes through the unchanged repair/reject path. Non-G-Max frames pass no
  // roster, so their dropped-digit protection is identical to before.
  const frameIsGmaxForSanity = variantLooksGmax || strideLooksGmax;
  // Any curated variant whose roster we can look up gets a validLowIds allow-set
  // so the sanity pass can distinguish a real low-ID member from a
  // dropped-trailing-digit misread. G-Max keeps its special-case path (its low
  // members #3/#6/#9 are all Kanto starters that would otherwise be rejected).
  // For non-G-Max: use the variant's own curated roster if available; for the
  // main "pokemon" variant, all Kanto IDs 1-9 are legal.
  const frameVariant = chipVariantForStride;
  let validLowIds: Set<number> | undefined = undefined;
  if (frameIsGmaxForSanity) {
    const roster = curatedRosterFor("gmax");
    if (roster) validLowIds = new Set(roster.filter((id) => id >= 1 && id <= 9));
  } else if (frameVariant === "pokemon") {
    // Main dex — all low IDs 1-9 are Kanto starters, all real.
    validLowIds = new Set([1, 2, 3, 4, 5, 6, 7, 8, 9]);
  } else if (frameVariant != null) {
    // Curated variant — the variant's own roster tells us which low IDs are real.
    const roster = curatedRosterFor(frameVariant);
    if (roster) validLowIds = new Set(roster.filter((id) => id >= 1 && id <= 9));
  }
  // Enrich the sanity input with grid COLUMN (from the 1-based slot index and
  // the frame's stride) and per-cell sprite confidence so the Root Cause C
  // appended-digit ladder guard can detect `7`→`70`→`77` siblings within one
  // column at near-identical sprite confidence. The kept objects preserve the
  // VariantCellObservation shape; the extra fields are optional and ignored
  // downstream.
  const sanityInput = observations.map((obs) => ({
    ...obs,
    column:
      obs.slotIndex != null && gridStride > 0
        ? (obs.slotIndex - 1) % gridStride
        : undefined,
  }));
  // A tab WITHOUT a curated roster (pokemon / shiny / lucky / perfect / xxs /
  // xxl) renders the whole dex, so its grid is dense: consecutive ids in
  // reading order. The curated
  // tabs (mega/gmax/shadow/purified) render a sparse subset with arbitrary gaps
  // between adjacent slots. Only the former can be checked positionally, which
  // is the precondition of the slot-ladder consensus guard.
  //
  // The chip is exactly what a smeared frame tends to lose, so "no chip" cannot
  // mean "no guard" — that would disarm it on the frames it exists for. We take
  // both signals we have that do not depend on this frame's chip read: a
  // curated chip variant when there IS one, and the 3-column geometry that only
  // G-Max uses. A chip-less frame on some other curated tab is the residual
  // case; it is inert, because a frame with no resolved variant is held by the
  // session's variant gate and writes nothing either way. Re-arming this on the
  // main "pokemon" dex is the point: validLowIds is populated there, which used
  // to disable the guard.
  const denseGrid =
    !strideLooksGmax && (frameVariant == null || curatedRosterFor(frameVariant) == null);
  const sanity = sanitizeFrameObservations(sanityInput, { validLowIds, denseGrid });
  if (sanity.repaired > 0 || sanity.rejected > 0) {
    // eslint-disable-next-line no-console
    console.log("[dex-scan] ocr sanity", {
      region: frame.activeRegion,
      repaired: sanity.repaired,
      rejected: sanity.rejected,
      ladderRejected: sanity.ladderRejected,
      slotLadderRejected: sanity.slotLadderRejected,
      duplicateRejected: sanity.duplicateRejected,
    });
  }
  observations.length = 0;
  observations.push(...sanity.kept);
  numbersRead = observations.length;

  // Build 6 NARROWED frame-level write veto (telemetry 2026-06-21 / regression
  // 2026-06-24). A cell the OCR sanity pass had to REPAIR, or whose id is
  // DUPLICATED among the frame's present writes, is an unreliable read — the
  // run-2 device log applied `[10,1,1,8]` (slots 10/11/12/14 all OCR'd "1",
  // caught). The ORIGINAL veto demoted EVERY present cell whenever the frame
  // had ANY repair/reject/duplicate, which starved confirmation streaks: a
  // single noisy cell in an otherwise-rich frame dropped all 16 reads to
  // "missing", so real catches never accumulated and ZERO entries applied.
  //
  // We now demote ONLY the cells we have a specific reason to distrust:
  //   - the exact cells the sanity pass repaired (sanity.repairedIndices), and
  //   - every cell whose id is duplicated this frame (duplicatePresentIds).
  // A REJECT already removed its cell from `observations`, so it needs no
  // demotion here. Clean, untouched present cells flow through to the
  // confirmation path unchanged. Missing/unknown reads are never present so are
  // unaffected. This preserves "false writes are the #1 failure class" (each
  // demoted cell still has a concrete distrust reason) while not punishing the
  // honest cells that share a frame with a misread.
  const dupIds = duplicatePresentIds(observations, frameVariant);
  let demoted = 0;
  const repairedIndexSet = new Set(sanity.repairedIndices);
  for (let i = 0; i < observations.length; i += 1) {
    const obs = observations[i];
    if (obs.state !== "present") continue;
    const isRepaired = repairedIndexSet.has(i);
    const isDuplicate = dupIds.has(obs.dexNumber);
    if (isRepaired || isDuplicate) {
      obs.state = "missing";
      demoted += 1;
    }
  }
  if (demoted > 0) {
    // eslint-disable-next-line no-console
    console.log("[dex-scan] frame present-writes blocked (unreliable cells)", {
      region: frame.activeRegion,
      demoted,
      repaired: sanity.repaired,
      rejected: sanity.rejected,
      duplicateIds: dupIds.size,
    });
  }

  // PR-C Problem 1 — variant detection via two-signal vote.
  //
  // Signal A: bottom-tab-pill ROI OCR, lexicon-validated fuzzy match.
  // Signal B: screen-tint colour heuristic.
  // We vote between them and NEVER silently default to "pokemon" — when both
  // are inconclusive we fall back to the sticky last-known variant.
  //
  // The legacy `frame.activeFilter` keyword (read from the highlighted strip,
  // when the in-grid OCR happened to catch it) is folded in as an extra
  // tab-pill line so existing upload-path callers that don't supply ROI lines
  // keep working. If neither the vote nor the legacy filter resolves anything,
  // we use the caller's initialVariant as a last sticky source.
  // Build 2 / v1.4.6 — PoGo chip-row variant is the AUTHORITATIVE source for
  // which dex the user is scanning. When the OCR layer produced a chip signal
  // we honour it directly and short-circuit the legacy vote/sticky chain:
  //   - confidence ≥ 0.6 → route to the detected variant (canonical, not
  //     inferred). This is what makes a Lucky scan write `variant:"lucky"`
  //     even while the Nexus page shows Perfect.
  //   - confidence < 0.6 → DROP the frame: variant stays null so the session
  //     rejects it as "no_variant". We never fall back to "perfect" or to the
  //     Nexus-side filter here — corrupting the wrong dex is worse than a
  //     skipped frame.
  const chip = frame.variantChip;
  if (chip) {
    if (chip.variant != null && chip.confidence >= VARIANT_CHIP_MIN_CONFIDENCE) {
      const chipVariant = chip.variant;
      // Grid SHAPE, like the grid signature, is a fact about the page and must
      // be judged on the pre-dedupe count. A page whose every cell was already
      // applied still IS a 4x4 grid; calling it "unknown" dropped the variant,
      // which is what expired the sticky-variant hold in the 2026-07-20 trace.
      const chipMode: DexScanMode =
        signatureNumbersRead === 0 || lowConfidence ? "unknown" : "grid_4x4";
      const variant = chipMode === "unknown" ? null : chipVariant;
      return finalizeAdaptResult({
        frame,
        observations,
        overlayFencedMissing,
        numbersRead,
        rowsWithNumber,
        signatureNumbersRead,
        signatureRowsWithNumber,
        mode: chipMode,
        lowConfidence,
        variant,
        variantInferred: false,
        variantConfidence: chipMode === "unknown" ? 0 : chip.confidence,
        variantSource: chip.source === "chip-text" ? "tab-only" : "tint-only",
        variantDetail:
          chip.source === "chip-text"
            ? `${chipVariant} (chip-text ✓)`
            : `${chipVariant} (chip-hue ✓)`,
        variantDisagreement: false,
        variantHueAgrees: chip.hueAgrees ?? false,
        spriteGate,
        gateMarkers,
        pageColourFamily: opts.pageColourFamily,
      });
    }
    // colour-narrowed pill re-read (2026-06-19). The active-pill read FAILED
    // (`source:"none"`, variant null) — the JOHTO repro where the pill anchor
    // missed the capsule band. Rather than hard-drop an already region-resolved,
    // numbers-populated frame, recover the variant from the PoGo PAGE COLOUR
    // (NEVER from app_state — that corrupts the wrong dex). Only attempt this
    // when we actually read dex numbers and the frame is otherwise committable.
    if (
      chip.variant == null &&
      chip.source === "none" &&
      signatureNumbersRead > 0 &&
      !lowConfidence
    ) {
      const colourRouted = routeFromPageColour(opts);
      if (colourRouted) {
        return finalizeAdaptResult({
          frame,
          observations,
          overlayFencedMissing,
          numbersRead,
          rowsWithNumber,
          signatureNumbersRead,
          signatureRowsWithNumber,
          mode: "grid_4x4",
          lowConfidence,
          variant: colourRouted.variant,
          variantInferred: false,
          variantConfidence: colourRouted.confidence,
          variantSource: colourRouted.source,
          variantDetail: colourRouted.detail,
          variantDisagreement: false,
          spriteGate,
          gateMarkers,
          pageColourFamily: opts.pageColourFamily,
        });
      }
      // No family, or a multi-variant family whose wide re-read matched no
      // allowed token → keep the conservative drop. NEVER app_state.
    }
    // Low-confidence / unrecoverable chip read → drop, do NOT default to
    // perfect/sticky/app_state.
    return finalizeAdaptResult({
      frame,
      observations,
      overlayFencedMissing,
      numbersRead,
      rowsWithNumber,
      signatureNumbersRead,
      signatureRowsWithNumber,
      mode: "unknown",
      lowConfidence,
      variant: null,
      variantInferred: false,
      variantConfidence: 0,
      variantSource: "unresolved",
      variantDetail: "unknown (chip < 0.6, frame dropped)",
      variantDisagreement: false,
      spriteGate,
      gateMarkers,
      pageColourFamily: opts.pageColourFamily,
    });
  }

  const legacyFilterVariant = filterToVariant(frame.activeFilter);
  const tabLines: TabPillLine[] = [...(opts.tabPillLines ?? [])];
  if (frame.activeFilter && frame.activeFilter !== "caught" && frame.activeFilter !== "seen") {
    // The legacy filter already mapped to a variant keyword; feed it as a
    // moderate-confidence pill line so the vote can use it as Signal A.
    tabLines.push({ text: legacyFilterVariant, confidence: 0.6 });
  }
  const tab = resolveTabPillSignal(tabLines);
  // Dead-path prune (2026-06-19): the legacy `resolveTintSignal` colour vote was
  // already a no-op (returns null — no positive selection), so it never fed the
  // vote. It is removed here entirely; the vote is now tab-pill-OCR → sticky →
  // main-default. ALL colour work lives in the page colour-family VETO applied
  // downstream by the ONE unified resolver (`resolveTrustedVariant` →
  // `variantResolution.resolveVariant`), which this detected `variant` is fed
  // into in useDexLiveScan as a SIGNAL — never a competing colour authority.
  // `voteVariant` requires a tint signal; pass a permanently-inert one inline so
  // no colour can positively select here. (`opts.tintSample` is retained on the
  // options for the hook's call-site compatibility but is intentionally unused.)
  const tint: TintSignal = { variant: null, confidence: 0 };
  const sticky =
    opts.lastKnownVariant ??
    (opts.initialVariant && opts.initialVariant !== "pokemon"
      ? (opts.initialVariant as TabVariant)
      : null);
  const vote = voteVariant({ tab, tint, lastKnown: sticky });

  let variant: PokedexVariant | null = vote.variant;
  let variantInferred = vote.source === "sticky";
  const variantSource = vote.source;
  // Preserve the legacy behaviour for callers that pass neither ROI signal nor
  // tint: if the vote is unresolved but the legacy filter named a non-main
  // variant, honour it (inferred).
  if (variant == null && legacyFilterVariant !== "pokemon") {
    variant = legacyFilterVariant;
    variantInferred = true;
  }
  // Main-dex default. When we DID read dex numbers from a real grid but no
  // positive variant signal resolved (no tab-pill OCR, no tint sample, no
  // non-main legacy filter, no sticky), the only screen that produces that
  // shape is the un-filtered (main) Pokédex. Defaulting to "pokemon" here is
  // NOT "silently mislabelling a Lucky/Shiny screen" — those cases supply a
  // positive signal that is honoured above; reaching this point means there is
  // no such signal. Without this, a plain main-dex live scan resolves variant
  // null and the session rejects every frame as "no_variant", which is the
  // exact "no new entries to apply" bug on the most common screen.
  let mainDexDefaulted = false;
  if (variant == null && signatureNumbersRead > 0) {
    // The un-filtered Pokédex IS the main dex — this is a canonical reading,
    // not an inference, so variantInferred stays false (a Lucky/Shiny screen
    // would have supplied a positive signal handled above).
    variant = "pokemon";
    mainDexDefaulted = true;
  }
  let variantConfidence = mainDexDefaulted ? 0.5 : vote.confidence;
  const variantDetail = mainDexDefaulted ? "Main (default. No variant filter)" : vote.detail;
  const variantDisagreement = vote.disagreement;

  // Decide the screen mode. Today the legacy OCR only fires on the 4×4
  // grid; region-overview detection is a future capability that lands
  // when the per-cell classifier picks up the badges row. We surface
  // the honest label so the diagnostic panel never lies.
  let mode: DexScanMode;
  if (signatureNumbersRead === 0) {
    mode = "unknown";
  } else if (lowConfidence) {
    mode = "unknown";
  } else {
    mode = "grid_4x4";
  }

  return finalizeAdaptResult({
    frame,
    observations,
    overlayFencedMissing,
    numbersRead,
    rowsWithNumber,
    signatureNumbersRead,
    signatureRowsWithNumber,
    mode,
    lowConfidence,
    variant,
    variantInferred,
    variantConfidence,
    variantSource,
    variantDetail,
    variantDisagreement,
    spriteGate,
    gateMarkers,
    pageColourFamily: opts.pageColourFamily,
  });
}

/**
 * Build the final `DexLiveFrameResult` from a resolved variant decision +
 * the per-cell observations. Shared by the chip-signal short-circuit and the
 * legacy two-signal vote path so the gridSignature/note/counter tail lives in
 * exactly one place.
 *
 * Honours the contract: `mode: "unknown"` ⇒ variant suppressed to null and
 * variantConfidence zeroed (the caller skips it), while still reporting the
 * signal provenance for the diagnostic panel.
 */
function finalizeAdaptResult(args: {
  frame: PokedexGridFrame;
  observations: VariantCellObservation[];
  numbersRead: number;
  rowsWithNumber: Set<number>;
  /** Cells that carried a dex number, counted BEFORE the dump-on-apply skip. */
  signatureNumbersRead: number;
  /** Bottom-band "missing" assertions dropped by the overlay fence (2026-08-30). */
  overlayFencedMissing?: number;
  /** Grid rows that carried a dex number, counted BEFORE the dump-on-apply skip. */
  signatureRowsWithNumber: Set<number>;
  mode: DexScanMode;
  lowConfidence: boolean;
  variant: PokedexVariant | null;
  variantInferred: boolean;
  variantConfidence: number;
  variantSource: VariantSignalSource;
  variantDetail: string;
  variantDisagreement: boolean;
  variantHueAgrees?: boolean;
  spriteGate?: DexLiveFrameResult["spriteGate"];
  gateMarkers: DexFrameGateMarkers;
  /**
   * Pre-OCR page-colour family for THIS frame (e.g. "dex_mint"). Diagnostic-only
   * here: used to surface a stalled sole-variant page (the Purified-on-mint
   * detection gap, telemetry 2026-06-23). Never alters the resolved variant.
   */
  pageColourFamily?: string | null;
}): DexLiveFrameResult {
  const {
    frame, observations, numbersRead, rowsWithNumber, mode, lowConfidence,
    variantSource, variantDetail, variantDisagreement, variantHueAgrees,
    gateMarkers, pageColourFamily,
  } = args;

  let variant = args.variant;
  let variantInferred = args.variantInferred;
  let variantConfidence = args.variantConfidence;

  // Suppress the variant when we couldn't read enough to commit. The
  // caller treats null as "skip"; this keeps the contract simple. We keep
  // `variantConfidence`/`variantDetail` so the diagnostic panel can still show
  // what the signals saw even on a frame we won't apply.
  if (mode === "unknown") {
    variant = null;
    variantInferred = false;
    variantConfidence = 0;
  }

  // Purified-detection diagnostic (telemetry 2026-06-23). A sole-variant page
  // colour (dex_mint→purified, dex_peach→perfect, dex_shadow_purple→shadow) that
  // ends this frame with NO resolved variant is the exact stall the device run
  // showed: OCR saw "PURIFIED" in global text and the page colour locked to
  // dex_mint, yet the active variant never resolved. This logs WHY the page held
  // (numbers read, low-confidence, chip source/confidence) so the next device run
  // pinpoints whether the gap is the grid/number read or the chip read — WITHOUT
  // any unsafe full-frame-text fallback (the Nexus Dex overlay itself contains the
  // word PURIFIED, so text alone must never select). Pure observation: this never
  // changes the resolved variant or relaxes a gate.
  const soleColourVariant = soleVariantForColourFamily(pageColourFamily);
  if (soleColourVariant != null && variant == null) {
    // eslint-disable-next-line no-console
    console.log("[dex-scan] sole-colour variant held", {
      pageColourFamily,
      expectedVariant: soleColourVariant,
      region: frame.activeRegion,
      mode,
      numbersRead,
      lowConfidence,
      chipSource: frame.variantChip?.source ?? "none",
      chipVariant: frame.variantChip?.variant ?? null,
      chipConfidence: frame.variantChip?.confidence ?? 0,
    });
  }

  // trial/simplify-gates (2026-07-06): overlay-text write veto REMOVED.
  //
  // The Tesseract-era Run-3 Gate D demoted every present observation to
  // "missing" whenever the frame's GLOBAL OCR text carried a Nexus overlay/status
  // HUD phrase ("Scanning", "Hold steady", "dex entries", "Added:", "NEXUS DEX"),
  // on the assumption the OCR had sampled our own PiP card and the dex numbers
  // were phantom. With Apple Vision the reader sees THROUGH the PiP overlay's
  // transparency and reads the underlying grid cells cleanly (device log
  // 2026-07-06: 61 `present-writes blocked (overlay text)` on cells Vision had
  // read correctly). The veto is deleted; we keep the detection purely as
  // telemetry so the follow-up scan can count how many cells this unblocks.
  // (The Nexus-SDK GEOMETRIC overlap guard `nexusOverlayObscuredCells` is a
  // different signal and is untouched.)
  if (variant != null && containsNexusOverlayMarker(frame.globalOcrText)) {
    for (const obs of observations) {
      if (obs.state === "present") {
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] overlay veto removed", {
          yPct: obs.yPct ?? null,
          dexId: obs.dexNumber,
        });
      }
    }
  }

  // Run-3 Gate A — variant/region/dex membership validation (telemetry
  // 2026-06-21). The SINGLE write chokepoint validates that each present
  // observation's (variant, dexId) is a real member of the active variant's
  // curated roster. The region/range was already validated upstream
  // (resolveCellNationalDex + dexCrossCheck); this adds the roster-membership
  // check that nothing else does. It blocks the device run's G-Max Kanto #145
  // (Articuno — no Gigantamax form), #25 (Pikachu), and any OCR garbage id
  // (#122/#569) that happens to fall inside the region range. Variants without a
  // curated roster (pokemon/shiny/perfect/xxs/xxl) are not roster-restricted, so
  // this gate is inert for them — region validation alone governs those.
  if (variant != null) {
    for (const obs of observations) {
      if (obs.state !== "present") continue;
      const check = validateDexMember(variant, obs.dexNumber);
      if (!check.valid) {
        obs.state = "missing";
        gateMarkers.invalidDexMember += 1;
        // Reader self-heal: capture Shadow/Purified reads blocked purely because
        // the id isn't in the curated roster (the roster lags real releases).
        // The session confirms these across frames at high confidence before
        // reporting; a stale-roster block is exactly the case we want to catch.
        if (
          (variant === "shadow" || variant === "purified") &&
          check.reason === "not-in-curated-roster" &&
          obs.dexNumber != null
        ) {
          gateMarkers.blockedShadowReads.push({
            dexId: obs.dexNumber,
            variant,
            confidence: frame.confidence,
          });
        }
        // eslint-disable-next-line no-console
        console.log("[dex-scan] present-write blocked (invalid dex member)", {
          variant,
          region: frame.activeRegion,
          dexId: obs.dexNumber,
          slotIndex: obs.slotIndex ?? null,
          reason: check.reason,
        });
      }
    }
  }

  const note =
    mode === "unknown" && numbersRead === 0
      ? "No Pokédex numbers visible"
      : lowConfidence
      ? "Low confidence: hold steadier"
      : frame.warnings[0];

  const regionKnown =
    frame.activeRegion !== "unknown" &&
    !frame.warnings.includes("active_region_unknown");
  const regionRead = regionKnown && frame.regionReason !== "tab_column";
  const filterKnown = !frame.warnings.includes("active_filter_unknown");
  const progressInRange = isProgressInRange(frame.activeRegion, frame.progressTotal);

  const gridSignature: DexGridSignature = {
    warnings: [...frame.warnings],
    text: `${frame.activeRegion} ${frame.activeFilter}`,
    // "Was this a Pokédex grid?" is a question about the PAGE, so it is answered
    // with the pre-dedupe counts. Using the post-skip ones let a mostly-applied
    // page fail its own gate and strand the cells still waiting to commit.
    numbersRead: args.signatureNumbersRead,
    distinctRows: args.signatureRowsWithNumber.size,
    regionKnown,
    regionRead,
    filterKnown,
    progressInRange,
  };

  return {
    variant,
    variantInferred,
    region: frame.activeRegion,
    mode,
    confidence: frame.confidence,
    observations,
    cellsObserved: frame.cells.length,
    numbersRead,
    unknownCells: Math.max(0, frame.cells.length - numbersRead),
    // Both overlay guards (number ROI and sprite body) count into this — either
    // way the cell produced nothing, which is what the coaching needs to know.
    overlayObscuredCells: (frame.nexusOverlayObscuredCells ?? 0) + (args.overlayFencedMissing ?? 0),
    spriteGate: args.spriteGate ?? {
      autoApplied: 0,
      queuedForReview: 0,
      dropped: 0,
      legacyPassthrough: 0,
    },
    gateMarkers,
    note,
    gridSignature,
    variantConfidence,
    variantSource,
    variantDetail,
    variantDisagreement,
    variantHueAgrees,
  };
}

/**
 * Routing-side adapter onto the UNIFIED variant resolver
 * (`variantResolution.resolveVariant`). This used to be a hand-maintained copy
 * of the authority chain that drifted against the per-cell copy in
 * pokedexOcr.ts (`resolveFrameVariant`) — the exact split that shipped the
 * XXL-on-cyan zero-commit bug. Both now delegate to the ONE module, so the
 * routed variant and the committed cell variant can never diverge.
 *
 * This wrapper adds only the frame-mode contract: `mode: "unknown"` ⇒ variant
 * null (no writes). Everything else — veto-first, chip-text ≥ 0.80 primary,
 * hue corroboration, curated-page guard, HOLD/SKIP on both-vetoed — lives in
 * `resolveVariant`. A HOLD outcome maps to `variant: null` so the caller skips
 * the frame exactly as it does for an unreadable one.
 *
 * `variantOverride: true` is emitted on the `frame ingested` log so a device
 * trace can prove which frames chip-text overruled app_state.
 */
export function resolveTrustedVariant(
  result: Pick<DexLiveFrameResult, "mode"> & { numbersRead?: number },
  appStateVariant: PokedexVariant,
  chipText?: { variant: PokedexVariant; confidence: number; hueAgrees?: boolean } | null,
  // The pre-OCR gate's page-colour family for THIS frame (e.g. "dex_cyan").
  // VETO/CORROBORATION ONLY: it can reject a resolved variant impossible for the
  // page colour, but never positively select one. Undefined / non-dex families
  // make the veto inert, preserving the upload path.
  pageColourFamily?: string | null,
): { variant: PokedexVariant | null; variantInferred: boolean; variantOverride: boolean } {
  if (result.mode === "unknown") {
    // TINT-BOUNDARY RECOVERY (device 2026-06-24, Purified-on-cyan). The adapter
    // drops to mode:"unknown" when the active-pill read failed AND the page colour
    // is a multi-variant family whose wide re-read matched no token — the exact
    // shape of a Purified page misread as dex_cyan (purified is not a cyan token,
    // so it can NEVER be recovered by the colour-narrowed re-read). The adapter
    // deliberately has NO app_state, so only here — where app_state is available —
    // can we tell the boundary case apart from a genuinely unreadable frame: when
    // app_state NAMES the sole variant of a tint-adjacent family (i.e. the user
    // actively selected that filter) and the frame DID read numbers, recover it.
    // Everything else keeps the conservative drop (no app_state fallback).
    if (
      (result.numbersRead ?? 0) > 0 &&
      boundaryRecoveredVariant(pageColourFamily, appStateVariant) != null
    ) {
      const resolution = resolveVariant({ appStateVariant, chipText, pageColourFamily });
      if (resolution.variant != null) {
        return {
          variant: resolution.variant,
          variantInferred: false,
          variantOverride: resolution.variantOverride,
        };
      }
    }
    return { variant: null, variantInferred: false, variantOverride: false };
  }
  const resolution = resolveVariant({ appStateVariant, chipText, pageColourFamily });
  // HOLD/SKIP and chip/app-state alike: variant carries through (null on HOLD),
  // never inferred (both app_state and a chip-text override are authoritative
  // reads, not inferences).
  return {
    variant: resolution.variant,
    variantInferred: false,
    variantOverride: resolution.variantOverride,
  };
}

/**
 * PR b17f Change 5 — the log label for where a frame's trusted variant came
 * from. b17n amendment: when chip-text overrode app_state the label is
 * "chip-text"; when app_state provided the variant it is "app_state"; null
 * (All pill / dropped frame) is "none". Pure + exported for tests.
 */
export function variantSourceLabel(
  trustedVariant: PokedexVariant | null,
  variantOverride?: boolean,
): "chip-text" | "app_state" | "none" {
  if (trustedVariant == null) return "none";
  return variantOverride ? "chip-text" : "app_state";
}
